import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "../../lib/ctx";
import { api, img } from "../../lib/api";

const SLIDES = [
  { img: "/images/vintage-shop.jpg", tag: "Vintage", title: "Vintage Classics", sub: "Rare finds with honest condition labels", to: "/shop/vintage", cta: "Shop Vintage" },
  { img: "/images/gaming1.jpg", tag: "Game Top Up", title: "Instant Game Top-Up", sub: "Delivered after verified payment", to: "/game-top-up", cta: "Top Up Now" },
  { img: "/images/stadium2.jpg", tag: "Sales", title: "Special Offers", sub: "Limited-time deals across every category", to: "/special-offers", cta: "See Deals" },
];

const SIDE = [
  { img: "/images/gaming2.jpg", title: "Game Top-Up", sub: "Fast & secure", to: "/game-top-up" },
  { img: "/images/sneaker1.jpg", title: "Sales", sub: "Up to big savings", to: "/special-offers" },
];
const go = (to) => (/^https?:/.test(to || "") ? { href: to, target: "_blank", rel: "noopener noreferrer" } : null);

const Slide = ({ s, active, k }) => (
  <Link to={go(s.to) ? "#" : (s.to || "/")} onClick={(e) => { if (go(s.to)) { e.preventDefault(); window.open(s.to, "_blank", "noopener"); } }} className={`absolute inset-0 transition-opacity duration-700 ${active ? "opacity-100" : "pointer-events-none opacity-0"}`} data-testid={`hero-slide-${k}`}>
    <img src={img(s.img)} alt={s.title} className="h-full w-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#ee4d2d]/90 via-[#ee4d2d]/50 to-transparent" />
    <div className="absolute inset-y-0 left-0 flex max-w-md flex-col justify-center gap-3 p-6 sm:p-10">
      {s.tag && <span className="w-fit rounded-sm bg-white px-2 py-0.5 text-[11px] font-bold uppercase text-[#ee4d2d]">{s.tag}</span>}
      <h2 className="text-3xl font-extrabold leading-tight text-white sm:text-5xl">{s.title}</h2>
      <p className="text-sm text-white/90 sm:text-base">{s.sub}</p>
      {s.cta && <span className="mt-2 inline-flex w-fit items-center gap-2 rounded-sm bg-white px-4 py-2 text-sm font-semibold text-[#ee4d2d]">{s.cta} <ArrowRight className="h-4 w-4" /></span>}
    </div>
  </Link>
);

const SideBanner = ({ b, k }) => (
  <Link to={b.to || "/"} className="group relative block flex-1 overflow-hidden rounded-sm" data-testid={`hero-side-${k}`}>
    <img src={img(b.img)} alt={b.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#ee4d2d]/90 to-transparent" />
    <div className="absolute bottom-0 left-0 p-4">
      <p className="text-lg font-extrabold text-white">{b.title}</p>
      <p className="text-xs text-white/90">{b.sub}</p>
    </div>
  </Link>
);

export const HeroBanner = () => {
  const { t } = useLang();
  const [i, setI] = useState(0);
  const [slides, setSlides] = useState(SLIDES);
  const [side, setSide] = useState(SIDE);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    api.get("/catalog/meta").then((r) => {
      if (Array.isArray(r.data.hero_slides)) setSlides(r.data.hero_slides.filter((s) => s.img));
      if (Array.isArray(r.data.hero_side)) setSide(r.data.hero_side.filter((s) => s.img));
    }).catch(() => {}).finally(() => { setReady(true); });
  }, []);
  const n = slides.length;
  const move = (d) => setI((p) => (p + d + n) % n);
  useEffect(() => {
    if (n < 2) { return undefined; }
    const id = setInterval(() => { setI((p) => (p + 1) % n); }, 5000);
    return () => { clearInterval(id); };
  }, [n]);
  if (!ready) return <section className="mx-auto mt-4 max-w-7xl sm:px-8 lg:px-12" data-testid="hero-loading"><div className="mt-6 h-56 animate-pulse rounded-sm bg-[#fde3dc] sm:h-80" /></section>;
  if (!n && !side.length) return null;
  return (
    <section className="keep-dark mx-auto mt-4 max-w-7xl px-0 sm:px-8 lg:px-12" data-testid="hero-section">
      <p className="mb-2 px-4 text-[11px] font-bold uppercase tracking-[0.2em] text-[#ee4d2d] sm:px-0" data-testid="hero-eyebrow">{t("heroEyebrow")}</p>
      <div className="grid gap-1.5 lg:grid-cols-3">
        <div className="group relative h-56 overflow-hidden sm:h-80 sm:rounded-sm lg:col-span-2" data-testid="hero-carousel">
          {slides.map((s, k) => <Slide key={k} k={k} s={s} active={k === i % n} />)}
          <button onClick={() => move(-1)} aria-label="Previous" className="absolute left-0 top-1/2 z-10 -translate-y-1/2 bg-black/25 p-2 text-white opacity-0 transition-opacity group-hover:opacity-100" data-testid="hero-prev-btn"><ChevronLeft className="h-5 w-5" /></button>
          <button onClick={() => move(1)} aria-label="Next" className="absolute right-0 top-1/2 z-10 -translate-y-1/2 bg-black/25 p-2 text-white opacity-0 transition-opacity group-hover:opacity-100" data-testid="hero-next-btn"><ChevronRight className="h-5 w-5" /></button>
          <div className="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 gap-1.5">
            {n > 1 && slides.map((s, k) => <button key={k} onClick={() => setI(k)} aria-label={s.title} className={`h-2 rounded-full transition-[width,background-color] duration-300 ${k === i % n ? "w-5 bg-white" : "w-2 bg-white/50"}`} data-testid={`hero-dot-${k}`} />)}
          </div>
        </div>
        <div className="hidden flex-col gap-1.5 sm:flex sm:h-40 sm:flex-row lg:h-80 lg:flex-col">
          {side.map((b, k) => <SideBanner key={k} k={k} b={b} />)}
        </div>
      </div>
    </section>
  );
};
